
import React from 'react';
import { motion } from 'framer-motion';
import { Plane, Calendar, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';
import { getCityName } from '@/components/airportUtils';
import { fmtAmount, fmtCurrency } from '@/utils/money';

export default function FlightCard({ flight, onClick }) {
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-brand-500/10 text-brand-400 border-brand-500/20',
      confirmed: 'bg-green-500/10 text-green-400 border-green-500/20',
      completed: 'bg-sky-400/10 text-sky-300 border-sky-400/20',
      cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
      defaulted: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
      delinquent: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
    };
    return (
      <Badge className={`capitalize text-xs font-normal ${styles[status] || styles.pending}`}>
        {status}
      </Badge>
    );
  };

  return (
    <motion.div
      className="glass rounded-2xl p-5 hover-glow transition-all duration-500 cursor-pointer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => onClick && onClick(flight)}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">{flight.marketing_airline}</h3>
          <p className="text-sm" style={{color: 'var(--text-muted)'}}>Flight {flight.flight_number}</p>
        </div>
        {getStatusBadge(flight.status)}
      </div>

      <div className="flex items-center justify-between mb-5">
        <div className="text-center">
          <p className="text-xl font-bold text-white">{getCityName(flight.departure_city)}</p>
          <p className="text-sm" style={{color: 'var(--text-secondary)'}}>{flight.departure_time}</p>
        </div>
        
        <div className="flex-shrink-0 mx-4">
            <Plane className="w-5 h-5 text-white transform -rotate-45" style={{color: 'var(--text-muted)'}}/>
        </div>
        
        <div className="text-center">
          <p className="text-xl font-bold text-white">{getCityName(flight.arrival_city)}</p>
          <p className="text-sm" style={{color: 'var(--text-secondary)'}}>{flight.arrival_time}</p>
        </div>
      </div>

      {/* Seat Information */}
      {flight.selected_seats && Object.keys(flight.selected_seats).length > 0 && (
        <div className="mt-4 p-3 glass-light rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-[var(--accent-color)]" />
            <span className="text-sm font-medium text-white">Selected Seats</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
            {Object.entries(flight.selected_seats).map(([seatId, seatData]) => (
              <div key={seatId} className="flex items-center gap-2">
                <div className="w-5 h-5 bg-primary rounded-sm flex items-center justify-center text-white text-[10px] font-medium">
                  {seatId.slice(-1)}
                </div>
                <span className="text-gray-300">
                  {seatData.passenger?.first_name} {seatData.passenger?.last_name}
                </span>
              </div>
            ))}
          </div>
          {flight.seat_total_cost > 0 && (
            <div className="mt-2 pt-2 border-t border-white/10 text-xs text-gray-300">
              Seat fees: ${fmtAmount(flight.seat_total_cost)}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-white/10 pt-4 flex justify-between items-end">
        <div>
          <p className="text-xs" style={{color: 'var(--text-muted)'}}>Departure</p>
          <p className="text-white font-medium text-sm">
            {flight.departure_date ? format(parseISO(flight.departure_date), 'MMM d, yyyy') : 'N/A'}
          </p>
        </div>
        <div className="text-right">
            <p className="text-xl font-bold" style={{color: 'var(--accent-color)'}}>
              ${(flight.grand_total || flight.grand_total_with_hold || 0).toLocaleString()}
            </p>
             <p className="text-xs" style={{color: 'var(--text-muted)'}}>Grand Total</p>
             {(() => {
               const fd = flight.flightData || flight.flight_data || {};
               const reserveCashCents = fd._reserve_cash_payment_cents ?? fd.reserve_cash_payment_cents ?? 0;
               if (reserveCashCents > 0) {
                 return (
                   <p className="text-[10px] text-emerald-400/80 mt-0.5">
                     Reserve + ${fmtCurrency(reserveCashCents)} card
                   </p>
                 );
               }
               return null;
             })()}
        </div>
      </div>
    </motion.div>
  );
}
